import { type JSX, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon, type IconComponent } from '@/components/icons';
import { Badge, Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useFeature, useFeatures } from '@/hooks/useFeatures';
import { useMemberships, useSwitchTenant, type Membership } from '@/hooks/useMemberships';
import { useStatsOverview } from '@/hooks/useStats';
import { useTenantBranding } from '@/hooks/useTenantBranding';
import { formatCurrency } from '@/lib/format';
import { roleAtLeast } from '@/lib/permissions';
import { cn } from '@/lib/utils';

const SIDEBAR_STORAGE_KEY = 'ezsender:sidebar';
const SIDEBAR_GROUPS_STORAGE_KEY = 'ezsender:sidebar:groups';

interface NavItem {
  to: string;
  icon: IconComponent;
  labelKey: string;
  badge?: string;
  /** Restrict the item to specific tenant types. Omit for "everyone". */
  requireTenantType?: ReadonlyArray<'platform' | 'reseller' | 'customer'>;
  /** Minimum role required (mirrors backend RequireRoleMin). The
   *  hierarchy is viewer < operator < admin < owner. Omit for "any
   *  authenticated role within the allowed tenant types". */
  requireRoleMin?: 'viewer' | 'operator' | 'admin' | 'owner';
  /** Hide the item unless the named feature flag is on for the tenant
   *  (resolved by the /v1/features hook). Used for the Voice module
   *  during the alpha rollout. */
  requireFeature?: 'voice' | 'sms';
}

/** A collapsible parent that holds its own NavItems. The group's gate
 *  fields are inherited by every child for filtering — a group hidden
 *  by tenant_type / role / feature filters all its items the same way.
 *  We could let children override but the use case hasn't shown up yet.
 */
interface NavGroup {
  /** Stable key for the expand/collapse persisted state. Distinct from
   *  labelKey because translations can change but the storage key
   *  shouldn't. */
  key: string;
  labelKey: string;
  icon: IconComponent;
  items: NavItem[];
  requireTenantType?: ReadonlyArray<'platform' | 'reseller' | 'customer'>;
  requireRoleMin?: 'viewer' | 'operator' | 'admin' | 'owner';
  requireFeature?: 'voice' | 'sms';
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

interface NavSection {
  labelKey: string;
  /** Entries can be flat NavItems or NavGroups (caret + nested children).
   *  Mixing both in the same section is supported — Voice for example
   *  has Campaigns at top level + nested IVR / Identity groups. */
  items: NavEntry[];
}

const SECTIONS: NavSection[] = [
  // Top-level Dashboard — only for customer tenants. Resellers / platform
  // see /admin/dashboard from the sec_admin section.
  {
    labelKey: 'sec_overview',
    items: [
      {
        to: '/dashboard',
        icon: Icon.home,
        labelKey: 'nav_dashboard',
        requireTenantType: ['customer'],
      },
    ],
  },
  // SMS — Campaigns is the headline action; Inbox + Audience are
  // collapsible groups so the sidebar isn't a wall of items. Every item
  // is gated on the per-tenant `sms` feature (tenants.feature_sms, mig
  // 097): voice-only customers have it off and must not see the SMS
  // module at all. The flag existed since 097 but nothing consumed it —
  // a voice-only tenant kept a full SMS sidebar.
  {
    labelKey: 'sec_sms',
    items: [
      { to: '/campaigns', icon: Icon.campaigns, labelKey: 'nav_campaigns', requireFeature: 'sms' },
      {
        key: 'sms-inbox',
        labelKey: 'nav_grp_inbox',
        icon: Icon.chat,
        requireFeature: 'sms',
        items: [
          { to: '/messages', icon: Icon.chat, labelKey: 'nav_messages', requireFeature: 'sms' },
          { to: '/inbound', icon: Icon.bell, labelKey: 'nav_inbound', requireFeature: 'sms' },
        ],
      },
      // Contact List + Sender IDs promoted out of the old collapsible
      // "Audience" group to flat items. Contact lists are cross-channel
      // (voice campaigns import the same lists) so the identical item
      // also appears under VOICE below, both pointing at /contacts so the
      // upload flow is the same in both places. Sender IDs is SMS-specific
      // (10DLC) and stays here only.
      { to: '/contacts', icon: Icon.contacts, labelKey: 'nav_contact_list', requireFeature: 'sms' },
      { to: '/senders', icon: Icon.hash, labelKey: 'nav_senders', requireFeature: 'sms' },
    ],
  },
  // Voice — same shape as SMS. SIP Trunks moved to sec_admin since
  // customers route via rate cards now (PR #200+); the only voice
  // infrastructure they touch is their own caller IDs and audio.
  {
    labelKey: 'sec_voice',
    items: [
      // Live dashboard first: the screen an operator watches while campaigns
      // dial. Read access is operator+; the page hides its actions below
      // admin. Backed by GET /v1/voice/live (tenant-scoped).
      {
        to: '/voice/live',
        icon: Icon.eye,
        labelKey: 'nav_voice_live',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      // Setup-before-launch order: DIDs and caller-id pools are the
      // prerequisites an operator configures before their first
      // campaign, so they sit above Campaigns instead of buried in
      // the IVR group (user request 2026-07-23).
      {
        to: '/voice/dids',
        icon: Icon.phone,
        labelKey: 'nav_voice_dids',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      {
        to: '/voice/caller-id-pools',
        icon: Icon.hash,
        labelKey: 'nav_voice_caller_id_pools',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      // Same cross-channel Contact List as under SMS (identical /contacts
      // page + upload flow). A voice campaign needs a contact list too, so
      // surface it here among the pre-launch prerequisites. Voice-gated +
      // operator+ to match the rest of the section.
      {
        to: '/contacts',
        icon: Icon.contacts,
        labelKey: 'nav_contact_list',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      {
        to: '/voice/campaigns',
        icon: Icon.campaigns,
        labelKey: 'nav_voice_campaigns',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      // Reports — single entry; the inbound report is now the "Inbound"
      // tab inside this page (the standalone /voice/inbound item was
      // removed and that route redirects to /voice/reports?tab=inbound).
      {
        to: '/voice/reports',
        icon: Icon.chart,
        labelKey: 'nav_voice_reports',
        requireFeature: 'voice',
        requireRoleMin: 'operator',
      },
      {
        key: 'voice-ivr',
        labelKey: 'nav_grp_ivr',
        icon: Icon.cmd,
        requireFeature: 'voice',
        requireRoleMin: 'operator',
        items: [
          {
            to: '/voice/flows',
            icon: Icon.code,
            labelKey: 'nav_voice_flows',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/assets',
            icon: Icon.speaker,
            labelKey: 'nav_voice_assets',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/ai-agents',
            icon: Icon.bot,
            labelKey: 'nav_voice_ai_agents',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/business-hours',
            icon: Icon.calendar,
            labelKey: 'nav_voice_business_hours',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          // Daily-review inboxes below the one-time config pages:
          // the Leads mini-CRM (workflow) first, then analytics results,
          // then the voicemail/recording archives.
          {
            to: '/voice/ai-agents/leads',
            icon: Icon.contacts,
            labelKey: 'nav_voice_ai_agent_leads',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/ai-agents/insights',
            icon: Icon.chart,
            labelKey: 'nav_voice_ai_agent_results',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/voicemails',
            icon: Icon.inbox,
            labelKey: 'nav_voice_voicemails',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/recordings',
            icon: Icon.microphone,
            labelKey: 'nav_voice_recordings',
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/voice/webhook-secret',
            icon: Icon.cog,
            labelKey: 'nav_voice_webhook_secret',
            requireFeature: 'voice',
            requireRoleMin: 'admin',
          },
        ],
      },
    ],
  },
  // Account — formerly "GROWTH". Reports + Balance + the
  // tenant-scoped developer surface (API keys + webhooks). These two
  // last items used to live under sec_admin's admin-workspace group
  // (platform/reseller only), but they manage the CURRENT tenant's
  // own API keys and webhook endpoints — that's a customer-facing
  // self-serve concern, not a platform-admin one. Eleven Solutions
  // hit this on 2026-05-27: their account was platform_admin = false
  // so the entire sec_admin section was hidden and they couldn't
  // reach /developers at all from the sidebar.
  {
    labelKey: 'sec_account',
    items: [
      { to: '/reports', icon: Icon.chart, labelKey: 'nav_reports' },
      { to: '/billing', icon: Icon.wallet, labelKey: 'nav_billing' },
      { to: '/developers', icon: Icon.code, labelKey: 'nav_dev' },
      { to: '/webhooks', icon: Icon.bell, labelKey: 'nav_webhooks' },
    ],
  },
  // Admin section — 3 flat top-level (Overview / Customers / Earnings)
  // for the daily-traffic items, then 4 collapsible groups for catalog
  // and infrastructure. Layout from option A in the design conversation.
  {
    labelKey: 'sec_admin',
    items: [
      {
        to: '/admin/dashboard',
        icon: Icon.home,
        labelKey: 'nav_admin_dashboard',
        requireTenantType: ['platform', 'reseller'],
      },
      {
        to: '/admin/customers',
        icon: Icon.contacts,
        labelKey: 'nav_admin_customers',
        requireTenantType: ['platform', 'reseller'],
      },
      {
        // Self-service sign-up review queue. Platform-only: approving
        // provisions a top-level customer, which the API restricts to the
        // platform tenant (reseller admins get 403).
        to: '/admin/signups',
        icon: Icon.inbox,
        labelKey: 'nav_admin_signups',
        requireTenantType: ['platform'],
      },
      {
        to: '/admin/earnings',
        icon: Icon.chart,
        labelKey: 'nav_admin_earnings',
        requireTenantType: ['platform', 'reseller'],
      },
      // Operations — unified read-only command center: System Health (stack +
      // worker monitor) and Live Ops (per-campaign CPS/throughput) on one
      // screen. Replaces the former separate "Live Ops" + "System Health"
      // entries. Platform/reseller only; not feature-gated on the caller's own
      // tenant since a platform admin monitoring other tenants may not have
      // voice on their own. The API further gates the health data at admin+
      // (RequireRoleMin("admin")).
      {
        to: '/admin/ops',
        icon: Icon.eye,
        labelKey: 'nav_admin_ops',
        requireTenantType: ['platform', 'reseller'],
      },
      // SMS pricing — Plans + Rate cards + Rates. Plans is shared with
      // Voice pricing too (cross-channel concept) but lives here as the
      // canonical entry point. Operator+ for read; mutations gated
      // server-side at admin+.
      {
        key: 'admin-sms-pricing',
        labelKey: 'nav_grp_sms_pricing',
        icon: Icon.wallet,
        requireTenantType: ['platform', 'reseller'],
        requireRoleMin: 'operator',
        items: [
          {
            to: '/admin/plans',
            icon: Icon.pkg,
            labelKey: 'nav_admin_plans',
            requireTenantType: ['platform', 'reseller'],
            requireRoleMin: 'operator',
          },
          {
            to: '/admin/rate-cards',
            icon: Icon.wallet,
            labelKey: 'nav_admin_rates',
            requireTenantType: ['platform', 'reseller'],
            requireRoleMin: 'operator',
          },
          {
            to: '/admin/rates',
            icon: Icon.chart,
            labelKey: 'nav_admin_rates_all',
            requireTenantType: ['platform', 'reseller'],
            requireRoleMin: 'operator',
          },
        ],
      },
      // Voice pricing — Plans (cross-channel, same /admin/plans link as
      // SMS), Voice rate cards, Voice rates (flat platform-wide list),
      // Voice catalog (Sprint 5 — list+toggle of TTS voices exposed in
      // the AI Agent picker).
      {
        key: 'admin-voice-pricing',
        labelKey: 'nav_grp_voice_pricing',
        icon: Icon.phone,
        requireTenantType: ['platform'],
        requireFeature: 'voice',
        items: [
          {
            to: '/admin/plans',
            icon: Icon.pkg,
            labelKey: 'nav_admin_plans',
            requireTenantType: ['platform'],
          },
          {
            to: '/admin/voice/rate-cards',
            icon: Icon.phone,
            labelKey: 'nav_admin_voice_rate_cards',
            requireTenantType: ['platform'],
            requireFeature: 'voice',
          },
          {
            to: '/admin/voice/rates',
            icon: Icon.chart,
            labelKey: 'nav_admin_voice_rates',
            requireTenantType: ['platform'],
            requireFeature: 'voice',
          },
          {
            to: '/admin/voice/catalog',
            icon: Icon.speaker,
            labelKey: 'nav_admin_voice_catalog',
            requireTenantType: ['platform'],
            requireFeature: 'voice',
          },
        ],
      },
      // Routing — carriers (SMS providers) + SIP Trunks (Voice).
      // Platform-only on both — trunks were moved out of the customer-
      // facing VOICE section in PR #205 since rate-card routing made
      // them infrastructure (PR #200+).
      {
        key: 'admin-routing',
        labelKey: 'nav_grp_routing',
        icon: Icon.cog,
        requireTenantType: ['platform'],
        items: [
          {
            to: '/admin/providers',
            icon: Icon.cog,
            labelKey: 'nav_admin_providers',
            requireTenantType: ['platform'],
          },
          {
            to: '/voice/trunks',
            icon: Icon.phone,
            labelKey: 'nav_voice_trunks',
            requireTenantType: ['platform'],
            requireFeature: 'voice',
            requireRoleMin: 'operator',
          },
          {
            to: '/admin/workers',
            icon: Icon.chart,
            labelKey: 'nav_admin_workers',
            requireTenantType: ['platform'],
          },
        ],
      },
      // SpamGuard — campaign anti-spam screening. Platform-only safety
      // infrastructure: the held-campaign review queue + the operator
      // keyword rules. Tenants never see any of this (and never see the
      // numeric spam score) by design.
      {
        key: 'admin-spamguard',
        labelKey: 'nav_grp_spamguard',
        icon: Icon.warn,
        requireTenantType: ['platform'],
        items: [
          {
            to: '/admin/spam-review',
            icon: Icon.warn,
            labelKey: 'nav_admin_spam_review',
            requireTenantType: ['platform'],
          },
          {
            to: '/admin/spam-keywords',
            icon: Icon.hash,
            labelKey: 'nav_admin_spam_keywords',
            requireTenantType: ['platform'],
          },
        ],
      },
      // Workspace — branding, sender pool, audit log + dev tools +
      // settings. Mostly tenant-scoped admin features that don't fit
      // pricing or routing.
      {
        key: 'admin-workspace',
        labelKey: 'nav_grp_workspace',
        icon: Icon.cog,
        items: [
          {
            to: '/admin/branding',
            icon: Icon.eye,
            labelKey: 'nav_admin_branding',
            requireTenantType: ['platform', 'reseller'],
          },
          {
            to: '/admin/senders',
            icon: Icon.hash,
            labelKey: 'nav_admin_senders',
            requireTenantType: ['platform', 'reseller'],
          },
          {
            to: '/admin/audit',
            icon: Icon.eye,
            labelKey: 'nav_admin_audit',
            requireTenantType: ['platform', 'reseller'],
          },
          { to: '/settings', icon: Icon.cog, labelKey: 'nav_settings' },
        ],
      },
    ],
  },
];

function readCollapsedFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function persistCollapsed(collapsed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
  } catch {
    // Storage may be disabled (private mode) — fall back to in-memory state only.
  }
}

// Per-group expand/collapse state, persisted as a JSON-serialized
// string[] of OPEN group keys. Default = closed (matching the principle
// that the sidebar should be light by default; the user opens what they
// use). The active route auto-opens its parent group on mount via
// readOpenGroups + the "active path" sweep in the component.
function readOpenGroups(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((s) => typeof s === 'string'));
  } catch {
    // ignore
  }
  return new Set();
}

function persistOpenGroups(open: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify([...open]));
  } catch {
    // ignore
  }
}

/**
 * Inline brand mark — verbatim translation of `<EzMark>` in
 * `design/app-shell.jsx`. Lives here because it is currently only used by the
 * sidebar; promote to `components/brand-mark.tsx` once a second caller appears.
 *
 * White-label override: when the reseller has set a `brand_logo_url`,
 * we render that image in the same square slot (with object-contain so
 * tall logos don't stretch). The label next to it falls back to the
 * default "ezsender" wordmark — replacing it would require an
 * uploaded "logo + wordmark" combo and a second column on the form.
 */
function BrandMark({ showLabel, logoUrl }: { showLabel: boolean; logoUrl: string }): JSX.Element {
  return (
    <div className="inline-flex items-center gap-2.5">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="logo"
          width={26}
          height={26}
          className="rounded-md object-contain"
          // Hide on load failure so the layout keeps the empty 26x26
          // slot but doesn't show the broken-image glyph. The next
          // visit re-runs the request anyway via React Query.
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : (
        <svg width={26} height={26} viewBox="0 0 64 64" fill="none" aria-label="EzSender">
          <circle cx="32" cy="32" r="30" fill="var(--ink-2)" />
          <rect x="14" y="14" width="36" height="36" rx="8" fill="var(--volt)" />
          <path
            d="M22 38 L42 26 M42 26 L36 26 M42 26 L42 32"
            stroke="var(--volt-ink)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {showLabel && (
        <span className="display text-[17px] font-semibold tracking-tight text-text">
          ezsender
        </span>
      )}
    </div>
  );
}

interface SidebarProps {
  /** Optional override — when omitted the component reads/writes its own state. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed: collapsedProp, onCollapsedChange }: SidebarProps): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() =>
    readCollapsedFromStorage(),
  );

  // Shares the ['stats', 'overview'] cache key with the Dashboard, so when
  // both are mounted only one request goes to /v1/stats/overview every 30s.
  const stats = useStatsOverview();
  const balance = stats.data?.balance;
  const { user } = useAuth();
  const tenantType = user?.tenantType ?? '';
  const isAgent = user?.role === 'agent';
  // Reseller's logo override — empty string when nothing's set, in
  // which case BrandMark renders the default EzSender SVG.
  const branding = useTenantBranding();
  const brandLogoUrl = (branding.data?.logo_url ?? '').trim();

  // Filter nav items by tenant type. Sections that end up empty are also
  // dropped so we don't render a stray section header.
  //
  // Agents are a special case: they don't fit into the platform /
  // reseller / customer split (they live on the platform tenant but
  // shouldn't see ANY of the platform admin nav), so we replace the
  // sections entirely with a single "My commissions" entry. The
  // RequireAgentRoute guard already blocks them from typing other
  // URLs; this just keeps the UI consistent.
  const userRole = user?.role;
  // Feature flags — gates voice nav items + sections. Defaults to false
  // during the initial /v1/features fetch so the items stay hidden until
  // we know they should appear (no flicker in either direction).
  const voiceOn = useFeature('voice');
  // SMS is the default module (column DEFAULT TRUE), so while /v1/features
  // is still in flight treat it as ON: blanking the whole SMS section for a
  // beat on every load would flicker for the majority of tenants. Voice-only
  // tenants (feature_sms=false) lose the SMS items the moment the flags land.
  const { data: features } = useFeatures();
  const smsOn = features?.sms ?? true;
  // Predicate shared by NavItem AND NavGroup filtering. Groups inherit
  // their gates to children; an item inside a group is also filtered
  // independently in case the group is permissive but the item isn't.
  const passesGates = (entry: NavEntry): boolean => {
    if (entry.requireTenantType) {
      if (tenantType === '') return false;
      if (
        !entry.requireTenantType.includes(
          tenantType as 'platform' | 'reseller' | 'customer',
        )
      ) {
        return false;
      }
    }
    // Role-min. For NavItems on /admin/* the implicit admin gate
    // mirrors the backend RequireRoleMin("admin") wrapper. Groups
    // don't have a `to`, so the implicit admin gate is skipped for
    // them — they declare requireRoleMin explicitly when needed.
    const isAdminPath = !isGroup(entry) && entry.to.startsWith('/admin/');
    const minRole = entry.requireRoleMin ?? (isAdminPath ? 'admin' : null);
    if (minRole && !roleAtLeast(userRole, minRole)) {
      return false;
    }
    if (entry.requireFeature === 'voice' && !voiceOn) {
      return false;
    }
    if (entry.requireFeature === 'sms' && !smsOn) {
      return false;
    }
    return true;
  };

  const visibleSections: NavSection[] = isAgent
    ? [
        {
          labelKey: 'sec_agent',
          items: [
            { to: '/agent/commissions', icon: Icon.wallet, labelKey: 'nav_my_commissions' },
          ],
        },
      ]
    : SECTIONS.map((section) => ({
        ...section,
        items: section.items
          .filter(passesGates)
          .map((entry) => {
            if (!isGroup(entry)) return entry;
            // Filter the group's children too — drop the whole group
            // when nothing's left so we don't render an empty caret.
            return { ...entry, items: entry.items.filter(passesGates) };
          })
          .filter((entry) => isGroup(entry) ? entry.items.length > 0 : true),
      })).filter((section) => section.items.length > 0);

  const collapsed = collapsedProp ?? internalCollapsed;

  // Per-group expand state. Init from storage; auto-open any group that
  // contains the current pathname so the active item is always visible
  // after a navigation/refresh.
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => readOpenGroups());
  useEffect(() => {
    setOpenGroups((prev) => {
      let next = prev;
      for (const section of visibleSections) {
        for (const entry of section.items) {
          if (!isGroup(entry)) continue;
          const matchesActive = entry.items.some((it) => location.pathname.startsWith(it.to));
          if (matchesActive && !prev.has(entry.key)) {
            if (next === prev) next = new Set(prev);
            next.add(entry.key);
          }
        }
      }
      return next;
    });
    // visibleSections is recomputed on every render but its content is
    // stable across pathname-only changes; the dep on pathname is what
    // we actually want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key: string): void => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persistOpenGroups(next);
      return next;
    });
  };

  useEffect(() => {
    if (collapsedProp === undefined) {
      persistCollapsed(internalCollapsed);
    }
  }, [collapsedProp, internalCollapsed]);

  const handleToggle = (): void => {
    const next = !collapsed;
    if (onCollapsedChange) {
      onCollapsedChange(next);
    } else {
      setInternalCollapsed(next);
    }
  };

  return (
    <aside
      className={cn(
        'flex flex-col flex-shrink-0 bg-surface-2 border-r border-line transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60',
      )}
      aria-label="Primary"
    >
      {/* Brand row — same height as the topbar so the two share a baseline. */}
      <div
        className={cn(
          'h-14 flex items-center border-b border-line',
          collapsed ? 'justify-center px-0' : 'justify-start px-4',
        )}
      >
        <button
          type="button"
          onClick={handleToggle}
          className="inline-flex items-center"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <BrandMark showLabel={!collapsed} logoUrl={brandLogoUrl} />
        </button>
      </div>

      {/* Tenant switcher — opens a dropdown of every tenant the user
          has a membership for; click swaps the JWT and reloads.
          Hidden for agents: they live on the platform tenant and have
          no other workspace to swap to. */}
      {!collapsed && !isAgent && <SidebarTenantSwitcher />}

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {visibleSections.map((section) => (
          <div key={section.labelKey} className="mb-4">
            {!collapsed && (
              <div className="px-3 pt-1 pb-1.5 text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
                {t(section.labelKey)}
              </div>
            )}
            <ul className="flex flex-col gap-0.5">
              {section.items.map((entry) =>
                isGroup(entry) ? (
                  <NavGroupRow
                    key={entry.key}
                    group={entry}
                    collapsed={collapsed}
                    open={openGroups.has(entry.key)}
                    onToggle={() => toggleGroup(entry.key)}
                    t={t}
                  />
                ) : (
                  <NavItemRow key={entry.to} item={entry} collapsed={collapsed} t={t} />
                ),
              )}
            </ul>
          </div>
        ))}
      </nav>

      {/* Balance widget — fed by /v1/stats/overview (shared cache with Dashboard).
          Hidden for agents: they don't have a tenant balance, the widget
          would always read 0 and the "Top up" button would 403 server-side. */}
      {!collapsed && !isAgent && (
        <div className="p-3 border-t border-line">
          <div className="p-3.5 bg-surface border border-line rounded-md">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] text-text-3 uppercase tracking-[0.04em]">
                {t('balance')}
              </span>
              <Badge variant="volt" className="text-[10px] px-1.5">
                USD
              </Badge>
            </div>
            <div className="num text-[22px] font-semibold text-text tracking-tight">
              {balance != null
                ? formatCurrency(balance, lang)
                : stats.isLoading
                  ? '—'
                  : '$0.00'}
            </div>
            <Button
              size="sm"
              className="w-full mt-2.5"
              onClick={() => navigate('/billing?topup=open')}
            >
              {t('top_up')}
            </Button>
          </div>
        </div>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Tenant switcher — dropdown of every membership the user has, click to
// reissue the JWT for that tenant. Hidden for users with only one
// membership (nothing to switch to). Replaces the old hardcoded
// "Salud Integral" placeholder.
// ---------------------------------------------------------------------------
function SidebarTenantSwitcher(): JSX.Element | null {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  const list = useMemberships();
  const switchTenant = useSwitchTenant();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click. Capture phase so it fires
  // before any nav-link click that would unmount the sidebar anyway.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (list.isLoading || !list.data) {
    return (
      <div className="px-3 pt-3 pb-1">
        <div className="w-full h-[42px] rounded-md bg-surface border border-line animate-pulse" />
      </div>
    );
  }

  const memberships = list.data.items;
  const current =
    memberships.find((m) => m.tenant_id === user?.tid) ?? memberships[0];
  if (!current) return null;

  const others = memberships.filter((m) => m.tenant_id !== current.tenant_id);

  const handlePick = (m: Membership): void => {
    if (m.tenant_id === current.tenant_id) {
      setOpen(false);
      return;
    }
    switchTenant.mutate(m.tenant_id);
  };

  return (
    <div className="px-3 pt-3 pb-1 relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switchTenant.isPending}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-surface border border-line rounded-md text-text text-[13px] hover:bg-surface-3 transition-colors disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <TenantInitial name={current.tenant_name} />
        <span className="flex-1 text-left leading-tight overflow-hidden">
          <span className="block font-semibold truncate">{current.tenant_name}</span>
          <span className="block text-[11px] text-text-3 capitalize">
            {t(`tenant_type_${current.tenant_type}`)}
          </span>
        </span>
        <Icon.chevronDown
          width="16"
          height="16"
          className={cn('text-text-3 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          className="absolute left-3 right-3 top-full mt-1 z-30 bg-surface border border-line rounded-md shadow-lg overflow-hidden"
          role="listbox"
        >
          {others.length === 0 ? (
            <div className="px-3 py-3 text-[12px] text-text-3 text-center">
              {t('tenant_switch_only_one')}
            </div>
          ) : (
            others.map((m) => (
              <button
                key={m.tenant_id}
                type="button"
                role="option"
                onClick={() => handlePick(m)}
                disabled={switchTenant.isPending}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-text hover:bg-surface-3 transition-colors disabled:opacity-60"
              >
                <TenantInitial name={m.tenant_name} />
                <span className="flex-1 leading-tight overflow-hidden">
                  <span className="block font-medium truncate">{m.tenant_name}</span>
                  <span className="block text-[11px] text-text-3 capitalize">
                    {t(`tenant_type_${m.tenant_type}`)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TenantInitial({ name }: { name: string }): JSX.Element {
  const initial = (name?.trim()?.[0] ?? '?').toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="w-[22px] h-[22px] rounded-md grid place-items-center text-volt-ink font-bold text-[12px] flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, var(--volt) 0%, var(--volt-600) 100%)' }}
    >
      {initial}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sidebar row primitives — extracted so the sidebar `nav` map stays tight
// and so groups + items share one stylebook (active-pill, rail indicator,
// icon coloring, collapsed-mode tooltip behavior).
// ---------------------------------------------------------------------------

interface NavItemRowProps {
  item: NavItem;
  collapsed: boolean;
  /** Indent the row — used for items rendered inside an expanded group
   *  so the nesting is visually obvious. Applies only when expanded. */
  inGroup?: boolean;
  t: (k: string) => string;
}

function NavItemRow({ item, collapsed, inGroup, t }: NavItemRowProps): JSX.Element {
  return (
    <li>
      <NavLink
        to={item.to}
        title={collapsed ? t(item.labelKey) : undefined}
        className={({ isActive }) =>
          cn(
            'group relative w-full flex items-center gap-2.5 rounded-md text-[13.5px] transition-colors border',
            collapsed ? 'justify-center py-2.5 px-0' : 'justify-start py-2 px-3',
            inGroup && !collapsed && 'pl-8', // indent + icon overlap so the line still aligns
            isActive
              ? 'bg-surface border-line text-text font-medium'
              : 'border-transparent text-text-2 hover:bg-surface-3',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute bg-volt rounded-[2px]',
                  collapsed
                    ? 'left-2 top-1/2 -translate-y-1/2 w-1 h-[18px]'
                    : 'left-[-1px] top-2 bottom-2 w-[2px]',
                )}
              />
            )}
            <item.icon
              width="18"
              height="18"
              className={cn('flex-shrink-0', isActive ? 'text-volt-600' : 'text-text-3')}
            />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t(item.labelKey)}</span>
                {item.badge && (
                  <Badge variant="gray" className="px-1.5 py-[1px] text-[11px]">
                    {item.badge}
                  </Badge>
                )}
              </>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

interface NavGroupRowProps {
  group: NavGroup;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  t: (k: string) => string;
}

function NavGroupRow({ group, collapsed, open, onToggle, t }: NavGroupRowProps): JSX.Element {
  // When the sidebar is collapsed, a group degrades into its first child
  // shown as a normal NavItemRow with the group icon — clicking it
  // navigates straight to the first item rather than trying to expand
  // anything (no room for the chevron when the rail is 64px wide).
  if (collapsed) {
    const first = group.items[0];
    if (!first) return <li />;
    return <NavItemRow item={{ ...first, icon: group.icon }} collapsed={collapsed} t={t} />;
  }
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-2.5 rounded-md text-[13.5px] transition-colors border border-transparent',
          'justify-start py-2 px-3 text-text-2 hover:bg-surface-3',
        )}
      >
        <group.icon width="18" height="18" className="flex-shrink-0 text-text-3" />
        <span className="flex-1 text-left">{t(group.labelKey)}</span>
        <span
          aria-hidden="true"
          className={cn(
            'text-text-4 transition-transform duration-150',
            open ? 'rotate-90' : '',
          )}
        >
          ›
        </span>
      </button>
      {open && (
        <ul className="flex flex-col gap-0.5 mt-0.5">
          {group.items.map((it) => (
            <NavItemRow key={it.to} item={it} collapsed={collapsed} inGroup t={t} />
          ))}
        </ul>
      )}
    </li>
  );
}
